import './App.scss';
import { ThemeProvider } from '@mui/material/styles';
import { createTheme, CssBaseline, useMediaQuery } from '@mui/material';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { EventType, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { useEffect, useState } from 'react';

import { HomePage, LoginPage, AdminPage } from './pages';
import { AuthHelper, CookieHelper } from '../../../WebCommon/src';
import { msalConfig } from './auth';

const msalInstance = new PublicClientApplication(msalConfig);

// Log auth failures for debugging
msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_FAILURE ||
      event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
    console.warn('MSAL auth failed:', event.error);
  }
});

/**
 * Clears MSAL cache from localStorage when auth state becomes corrupted.
 * This resolves "interaction_in_progress" and other stale state issues.
 */
function clearMsalCache(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('msal.')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => {
    console.log('Clearing stale MSAL cache key:', key);
    localStorage.removeItem(key);
  });
}

const App = () => {
  const [isMsalReady, setIsMsalReady] = useState(false);

  // Initialize MSAL and handle any pending redirect promises
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        // MSAL 3.x requires explicit initialization
        await msalInstance.initialize();

        // Handle any redirect response from previous auth flow
        // This is critical - without this, auth state can get stuck
        await msalInstance.handleRedirectPromise();

        setIsMsalReady(true);
      } catch (error) {
        console.error('MSAL initialization failed:', error);
        // Clear any corrupted cache state and retry
        clearMsalCache();
        try {
          await msalInstance.initialize();
          setIsMsalReady(true);
        } catch (retryError) {
          console.error('MSAL initialization failed after cache clear:', retryError);
        }
      }
    };

    initializeMsal();
  }, []);

  let uriBase: string;
  // In development, use Vite proxy to avoid CORS issues
  // Proxy: http://localhost:3000/api -> https://app.gemell.io/api
  if (window.location.hostname === "localhost") {
    // Use local proxy path - Vite will forward to production API
    uriBase = "/api/";
  }
  else {
    // Production deployment
    uriBase = "https://app.gemell.io/api/";
  }

  const hostBase = "gemell.io";
  const cookieHelper = new CookieHelper(hostBase);
  const authHelper = new AuthHelper(cookieHelper);;

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const darkTheme = createTheme({
    palette: {
      mode: prefersDarkMode ? "dark" : "light",
    },
  });

  const router = createBrowserRouter([{
    path: "/",
    element: <HomePage authHelper={authHelper} baseApiUrl={uriBase} />,
  }, {
    path: "/Login",
    element: <LoginPage authHelper={authHelper} baseApiUrl={uriBase} />,
  }, {
    path: "/LoginDevice",
    element: <LoginPage
      deviceLogin
      authHelper={authHelper} baseApiUrl={uriBase} />
  }, {
    path: "/Admin",
    element: <AdminPage authHelper={authHelper} baseApiUrl={uriBase} />
  }]);

  // Don't render until MSAL is initialized to prevent race conditions
  if (!isMsalReady) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          Loading...
        </div>
      </ThemeProvider>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <ThemeProvider theme={darkTheme}>
        <RouterProvider router={router} />
        <CssBaseline />
      </ThemeProvider>
    </MsalProvider>
  );
};

export default App;
